import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { Em, Reveal } from "./primitives";

function CtaButton({ label, url, onNavigate, dark }) {
  if (!label) return null;
  const handle = () => {
    if (!url) return;
    if (url.startsWith("#")) onNavigate?.(url);
    else window.open(url, url.startsWith("http") ? "_blank" : "_self", "noopener");
  };
  return (
    <button
      data-testid="custom-section-cta"
      onClick={handle}
      className={`group inline-flex w-fit items-center gap-2 rounded-full px-8 py-4 text-sm font-medium transition-colors duration-300 ${
        dark ? "bg-rose text-white hover:bg-rose-dark" : "bg-ink text-cream hover:bg-rose hover:text-white"
      }`}
    >
      {label}
      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
    </button>
  );
}

export default function CustomSection({ section, onNavigate }) {
  const c = section.content || {};
  const paras = String(c.body || c.text || "").split(/\n\n+/).filter(Boolean);

  if (section.type === "banner") {
    return (
      <section data-testid={`custom-section-${section.id}`} className="bg-ink py-14 text-cream">
        <Reveal className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-6 md:flex-row md:items-center md:px-12">
          <h2 className="font-serif text-3xl font-medium md:text-4xl"><Em text={c.heading || c.text} /></h2>
          <CtaButton label={c.button_label} url={c.button_url} onNavigate={onNavigate} dark />
        </Reveal>
      </section>
    );
  }

  if (section.type === "cta") {
    return (
      <section data-testid={`custom-section-${section.id}`} className="py-20">
        <Reveal className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="rounded-[32px] border border-rose/20 bg-rose/5 p-10 text-center md:p-16">
            <h2 className="font-serif text-3xl font-medium text-ink md:text-5xl"><Em text={c.heading} /></h2>
            {c.text && <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#5C5C5C] md:text-base">{c.text}</p>}
            <div className="mt-8 flex justify-center">
              <CtaButton label={c.button_label} url={c.button_url} onNavigate={onNavigate} />
            </div>
          </div>
        </Reveal>
      </section>
    );
  }

  if (section.type === "video") {
    const src = c.video ? imgUrl(c.video) : "";
    return (
      <section data-testid={`custom-section-${section.id}`} className="py-24">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <Reveal className="mb-10 text-center">
            {c.heading && <h2 className="font-serif text-4xl font-medium text-ink md:text-5xl"><Em text={c.heading} /></h2>}
            {c.description && <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#5C5C5C] md:text-base">{c.description}</p>}
          </Reveal>
          {src && (
            <Reveal>
              <video
                data-testid="video-block-player"
                src={src}
                poster={c.poster ? imgUrl(c.poster) : undefined}
                controls={c.controls !== false}
                autoPlay={!!c.autoplay}
                muted={c.muted !== false}
                loop={!!c.loop}
                playsInline
                preload="metadata"
                className="w-full rounded-3xl border border-line shadow-xl shadow-rose/5"
              />
            </Reveal>
          )}
        </div>
      </section>
    );
  }

  if (section.type === "gallery") {
    const images = (c.images || []).filter(Boolean);
    return (
      <section data-testid={`custom-section-${section.id}`} className="py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          {c.heading && (
            <Reveal className="mb-12">
              <h2 className="font-serif text-4xl font-medium text-ink md:text-5xl"><Em text={c.heading} /></h2>
            </Reveal>
          )}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            {images.map((img, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <img src={imgUrl(img)} alt={c.heading || "Gallery image"} loading="lazy" className="aspect-square w-full rounded-2xl border border-line object-cover" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (section.type === "image_text") {
    return (
      <section data-testid={`custom-section-${section.id}`} className="py-24">
        <div className={`mx-auto grid max-w-7xl items-center gap-12 px-6 md:px-12 lg:grid-cols-2`}>
          <Reveal className={c.flip ? "lg:order-2" : ""}>
            {c.image && <img src={imgUrl(c.image)} alt={c.heading || "Section image"} loading="lazy" className="w-full rounded-3xl border border-line object-cover" />}
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl font-medium text-ink md:text-5xl"><Em text={c.heading} /></h2>
            {paras.map((p, i) => (
              <p key={i} className="mt-4 max-w-lg text-sm leading-relaxed text-[#5C5C5C] md:text-base">{p}</p>
            ))}
            <div className="mt-8">
              <CtaButton label={c.button_label} url={c.button_url} onNavigate={onNavigate} />
            </div>
          </Reveal>
        </div>
      </section>
    );
  }

  // default: "text"
  return (
    <section data-testid={`custom-section-${section.id}`} className="py-24">
      <Reveal className="mx-auto max-w-3xl px-6 md:px-12">
        {c.heading && <h2 className="font-serif text-4xl font-medium text-ink md:text-5xl"><Em text={c.heading} /></h2>}
        {paras.map((p, i) => (
          <p key={i} className="mt-5 text-sm leading-relaxed text-[#5C5C5C] md:text-base">{p}</p>
        ))}
      </Reveal>
    </section>
  );
}
